import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function Grades() {
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [students, setStudents] = useState([])
  const [grades, setGrades] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selStudent, setSelStudent] = useState(null)
  const [examName, setExamName] = useState('')
  const [score, setScore] = useState('')
  const [total, setTotal] = useState('100')
  const [note, setNote] = useState('')

  useEffect(() => { fetchGroups() }, [])
  useEffect(() => { if (selectedGroup) { fetchStudents(); fetchGrades() } }, [selectedGroup])

  async function fetchGroups() {
    const { data } = await supabase.from('groups').select('*').order('grade_level').order('name')
    setGroups(data || [])
    if (data?.length) setSelectedGroup(data[0])
    setLoading(false)
  }

  async function fetchStudents() {
    const { data } = await supabase.from('students').select('*')
      .eq('group_id', selectedGroup.id).order('name')
    setStudents(data || [])
  }

  async function fetchGrades() {
    const { data: studs } = await supabase.from('students').select('id').eq('group_id', selectedGroup.id)
    if (!studs?.length) { setGrades([]); return }
    const { data } = await supabase.from('grades')
      .select('*, students(name)').in('student_id', studs.map(s => s.id))
      .order('created_at', { ascending: false })
    setGrades(data || [])
  }

  async function addGrade() {
    if (!selStudent) { alert('Please select a student'); return }
    if (!examName.trim()) { alert('Please enter exam name'); return }
    if (!score) { alert('Please enter the score'); return }
    const { error } = await supabase.from('grades').insert({
      student_id: selStudent.id, exam_name: examName.trim(),
      score: parseFloat(score), total: parseFloat(total) || 100,
      exam_date: new Date().toISOString().split('T')[0], note: note.trim()
    })
    if (error) { alert(error.message); return }
    fetchGrades(); setShowModal(false)
    setExamName(''); setScore(''); setTotal('100'); setNote(''); setSelStudent(null)
  }

  async function deleteGrade(id) {
    if (!window.confirm('Delete this exam record?')) return
    await supabase.from('grades').delete().eq('id', id)
    setGrades(prev => prev.filter(g => g.id !== id))
  }

  const pct = (s, t) => Math.round(s / t * 100)
  const pctClass = (p) => p >= 80 ? 'badge-success' : p >= 60 ? 'badge-warning' : 'badge-danger'

  if (loading) return <div className="empty">Loading...</div>

  return (
    <div>
      <div className="page-header">
        <h2>Exams</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Add Exam</button>
      </div>

      {/* Group selector */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
        {groups.map(g => (
          <button key={g.id} className={`chip ${selectedGroup?.id === g.id ? 'active' : ''}`}
            onClick={() => setSelectedGroup(g)}>
            {g.name}
          </button>
        ))}
      </div>

      {grades.length === 0 ? <div className="empty">No exam records yet</div> : (<>

        {/* Desktop table */}
        <div className="desktop-table">
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <table>
              <thead><tr>
                <th>Student</th><th>Exam</th><th>Score</th><th>%</th><th>Note</th><th></th>
              </tr></thead>
              <tbody>
                {grades.map(g => {
                  const p = pct(g.score, g.total)
                  return (
                    <tr key={g.id}>
                      <td style={{ fontWeight:500 }}>{g.students?.name}</td>
                      <td>{g.exam_name}</td>
                      <td>{g.score}/{g.total}</td>
                      <td><span className={`badge ${pctClass(p)}`}>{p}%</span></td>
                      <td style={{ color:'var(--text3)' }}>{g.note || '—'}</td>
                      <td><button className="btn btn-sm btn-danger" onClick={() => deleteGrade(g.id)}>Delete</button></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="mobile-cards">
          {grades.map(g => {
            const p = pct(g.score, g.total)
            return (
              <div key={g.id} className="mobile-card">
                <div className="mobile-card-row">
                  <div>
                    <div style={{ fontWeight:600, fontSize:15 }}>{g.students?.name}</div>
                    <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>{g.exam_name}</div>
                  </div>
                  <span className={`badge ${pctClass(p)}`} style={{ fontSize:14 }}>{p}%</span>
                </div>
                <div className="mobile-card-row" style={{ marginTop:8 }}>
                  <span style={{ fontSize:13, color:'var(--text3)' }}>Score: {g.score}/{g.total}</span>
                  <button className="btn btn-sm btn-danger" onClick={() => deleteGrade(g.id)}>Delete</button>
                </div>
                {g.note && <div style={{ fontSize:12, color:'var(--text3)', marginTop:6 }}>{g.note}</div>}
              </div>
            )
          })}
        </div>
      </>)}

      {/* Modal */}
      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">Add Exam Result</div>

            <div className="field">
              <label>Select Student *</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {students.map(s => (
                  <button key={s.id} className={`chip ${selStudent?.id === s.id ? 'active' : ''}`}
                    onClick={() => setSelStudent(s)}>{s.name}</button>
                ))}
              </div>
              {students.length === 0 && <div style={{ fontSize:13, color:'var(--danger)', marginTop:6 }}>No students in this group</div>}
            </div>

            <div className="field">
              <label>Exam Name *</label>
              <input value={examName} onChange={e => setExamName(e.target.value)} placeholder="e.g. October Exam" />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <div className="field" style={{ flex:1 }}>
                <label>Score *</label>
                <input type="number" value={score} onChange={e => setScore(e.target.value)} placeholder="0" min="0" />
              </div>
              <div className="field" style={{ flex:1 }}>
                <label>Out of</label>
                <input type="number" value={total} onChange={e => setTotal(e.target.value)} min="1" />
              </div>
            </div>
            <div className="field">
              <label>Note (optional)</label>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Any note..." />
            </div>

            <div className="modal-actions">
              <button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addGrade}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}